import { useMutation, useQuery } from '@apollo/client'
import { Button, Checkbox, Divider, Form, Input, message, Modal, Row, Select, Typography } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import gql from 'graphql-tag'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { useCallback } from 'react'
import { EVENEMENT_ADD_MUTATION } from '../../apollo/mutation/evenement.mutation'
import { EVENEMENTS } from '../../apollo/query/demande.query'
import { C_S_QUERY } from '../../apollo/query/personne.query'
import { AWS_S3_RESIZED_URL, AWS_S3_URL, COLOR } from '../../constants'
import { formatMembreCSOptions, useImperativeQuery } from '../../helper'
import { useAuth, useQueryCopro } from '../../lib/hooks'
import {
  D4_Conseilsyndical,
  D4_Evenementdemande_Set_Input,
  D4_Fn_View_Demande,
  D4_Fn_View_Evenements,
  D4_Modelecourrier,
  D4_Personne,
  D4_Societe,
} from '../../types'
import MsGraphAPI, { useMsGraphAPI } from '../Courriel/ms-graph.api'
import Thumbnail from '../Thumbnail'
import DebounceSelect from './DebounceSelect'

const COURRIEL_DESTINATAIRES_QUERY = gql`
  query CourrielDestinataires($trg: String!, $search: String!) {
    gestionnaires: d4_personne(
      where: {
        _and: [
          { dtype: { _eq: "Gestionnaire" } }
          { mail1: { _neq: "" } }
          { _or: [{ nom: { _ilike: $search } }, { prenom: { _ilike: $search } }, { mail1: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      nom
      prenom
      mail1
    }
    personnes: d4_personne(
      where: {
        _and: [
          { coproprietaire: { lot_coproprietaires: { lot: { copropriete: { trigramme: { _eq: $trg } } } } } }
          { mail1: { _neq: "" } }
          { _or: [{ nom: { _ilike: $search } }, { prenom: { _ilike: $search } }, { mail1: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      nom
      prenom
      mail1
      coproprietaire {
        matricules {
          permalink
        }
      }
    }
    fournisseurs: d4_societe(
      where: {
        _and: [
          { mail1: { _neq: "" } }
          { _or: [{ mail1: { _ilike: $search } }, { raisonsociale: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      raisonsociale
      mail1
    }
  }
`

interface Props {
  onOk: (() => void) | undefined
  onCancel: (() => void) | undefined
  visible: boolean
  evenements: Maybe<D4_Fn_View_Evenements[]>
  demande: D4_Fn_View_Demande
}

// `${demande.description} (${demande.id})`
interface SendMailData {
  body: string
  subject: string
  evenements: D4_Fn_View_Evenements[]
  to: { permalink: string; value: string }[]
  // cc: string[]
  demandeId: number
  coproId: number
}

// let isImg = (texte: string) => /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(texte ?? '')
let isPdf = (texte: string) => /\.(pdf)$/i.test(texte ?? '')

const sendEmail = async ({ subject, body, evenements, to, demandeId, coproId }: SendMailData) => {
  const mailsPromise = to.map((t) => {
    const data = {
      message: {
        subject: subject,
        body: {
          contentType: 'html',
          content:
            `${
              t.permalink &&
              `
              <div style="display: flex; gap: 8px; width: 100%; background-color: ${COLOR.lightestGrey}; padding: 4px; color: ${COLOR.green};">
                <div>
                  <h2>
                  Merci de ne pas répondre à ce mail directement et d’utiliser les boutons :
                  </h2>
                </div>
                <div style="display: flex; flex-direction: column; justify-content: center;">
                  <div style="display: flex; gap: 4px;">
                      <a href="https://vision.d4immobilier.fr/suivi_des_demandes.jsp?idDemande=${demandeId}&copro_id=${coproId}&permalink=${t.permalink}" style="padding: 8px 22px; color: white; cursor: pointer; background-color: ${COLOR.green};">
                        <strong>Voir</strong>
                      </a>
                      <a href="https://vision.d4immobilier.fr/suivi_des_demandes.jsp?idDemande=${demandeId}&copro_id=${coproId}&permalink=${t.permalink}" style="padding: 8px 22px; color: white; cursor: pointer; background-color: ${COLOR.green};">
                        <strong>Repondre</strong>
                      </a>
                  </div>
                </div>
              </div>`
            }
          ` +
            `
            <br />
            <div>Objet: ${subject}</div>
            <div>À: ${to.map(({ value }) => value).join(', ')}</div>
            <br />
            <br />
            ` +
            body.replaceAll('\n', '<br />') +
            '<br/><br/>' +
            evenements
              ?.map(
                (ev) =>
                  `<div>${ev.auteurnom} - ${ev.auteurprenom}<br />${
                    ev.fichier
                      ? `<a href="${AWS_S3_URL}${ev.texte}" target="_blank" >
                    <img width="150px" src="${AWS_S3_RESIZED_URL}resized-${ev.texte}${
                          isPdf(ev.texte ?? '') ? '.png' : ''
                        }" />
                  </a>`
                      : ev.texte
                  }</div><hr><br />`
              )
              .join(''),
        },
        toRecipients: [{ emailAddress: { address: t.value } }],
        // ccRecipients: cc?.map((personne) => ({ emailAddress: { address: personne.trim().toLocaleLowerCase() } })) ?? [],
      },
      saveToSentItems: 'true',
    }
    return MsGraphAPI.sendEmail({ data })
  })

  return Promise.all(mailsPromise)
  // const res = await MsGraphAPI.sendEmail({ data })
  // return res
}

const MODELE_COURRIER_QUERY = gql`
  query ModeleCourrier {
    modeles: d4_modelecourrier {
      corpsdetexte
      formuledepolitesse
      id
      objet
    }
  }
`

const ModalSelectEvents: React.FC<Props> = ({ visible, onOk, onCancel, evenements, demande }) => {
  const [form] = useForm()
  const {
    user: {
      personne: { nom, prenom },
    },
  } = useAuth()
  const [createEvenement] = useMutation<{}, { data: D4_Evenementdemande_Set_Input }>(EVENEMENT_ADD_MUTATION)
  const { data: modeleCourrier } = useQuery<{ modeles: D4_Modelecourrier[] }>(MODELE_COURRIER_QUERY)

  const search = useImperativeQuery<
    { personnes: D4_Personne[]; fournisseurs: D4_Societe[]; gestionnaires: D4_Personne[] },
    { trg: string; search: string }
  >(COURRIEL_DESTINATAIRES_QUERY)

  const searchCS = useImperativeQuery<{ d4_conseilsyndical: D4_Conseilsyndical[] }, { trg: string }>(C_S_QUERY)

  const [copro] = useQueryCopro(demande.coproprieteid)

  useMsGraphAPI()

  const handleFinish = useCallback(
    async (values: any) => {
      const filteredTo = values.to.filter(({ value }: any) => value?.trim())
      const data: SendMailData = {
        to: filteredTo, // ajouter le permalink
        // cc: values.cc,
        evenements: evenements?.filter((ev) => values.evt.includes(ev.id)) ?? ([] as D4_Fn_View_Evenements[]),
        subject: values.subject,
        body: values.body,
        demandeId: demande.id,
        coproId: copro?.id,
      }

      sendEmail(data)
      const res = await sendEmail(data)

      if (res.length) {
        message.success('Le mail a bien été envoyé')
        form.resetFields()
        createEvenement({
          variables: {
            data: {
              demande_id: demande.id,
              texte: `Envoi du mail\n\n<strong>De : ${nom} ${prenom}\nA : ${
                filteredTo?.map(({ value }: any) => value).join(', ') ?? ''
              }
              \nObjet : ${values.subject}</strong>\n\n${values.body}`,
            },
          },
          refetchQueries: [
            {
              query: EVENEMENTS,
              variables: { id: demande.id },
            },
          ],
        })
        onOk?.()
      } else {
        message.error("Une erreur est survenue lors de l'envoi du mail")
      }
    },
    [copro?.id, createEvenement, demande.id, evenements, form, nom, onOk, prenom]
  )

  const handleSearch = useCallback(
    async (v: string): Promise<any[]> => {
      let nextOptions = []
      if (v && v === 'cs') {
        const res = await searchCS({ trg: copro?.trigramme ?? '' })
        const [opt] = formatMembreCSOptions(res.data.d4_conseilsyndical)
        nextOptions = opt
        return Promise.resolve(nextOptions)
      }

      if (!v || v.length < 3) return Promise.resolve([])
      const res = await search({ trg: copro?.trigramme ?? '', search: '%' + v + '%' })

      res.data.gestionnaires.length > 0 &&
        nextOptions.push({
          key: 'gestionnaires',
          label: 'Gestionnaires',
          options: res.data.gestionnaires.map((ges) => ({
            value: ges.mail1,
            label: ges.nom + ' ' + ges.prenom,
            key: ges.id,
          })),
        })
      res.data.personnes.length > 0 &&
        nextOptions.push({
          key: 'personnes',
          label: 'Copropriétaires',
          options: res.data.personnes.map((p) => ({
            value: p.mail1,
            label: p.nom + ' ' + p.prenom,
            key: p.id,
            permalink: p.coproprietaire?.matricules?.[0].permalink,
          })),
        })
      res.data.fournisseurs.length > 0 &&
        nextOptions.push({
          key: 'fournisseurs',
          label: 'Fournisseurs',
          options: res.data.fournisseurs.map((four) => ({
            value: four.mail1,
            label: four.raisonsociale,
            key: four.id,
          })),
        })

      return Promise.resolve(nextOptions)
    },
    [copro?.trigramme, search, searchCS]
  )

  const optionsFilter = (fieldName: string) => (options: OptionsType) => {
    return options.map((opt) => ({
      ...opt,
      options: opt.options?.filter((o) => !form.getFieldValue(fieldName)?.includes(o.value)),
    }))
  }

  return (
    <Modal
      title="Envoyer un mail"
      visible={visible}
      footer={false}
      onCancel={onCancel}
      cancelText="Annuler"
      width="66%"
    >
      <Form
        form={form}
        onFinish={handleFinish}
        labelCol={{ span: 2 }}
        initialValues={{
          body: 'Bonjour,\n\nSincères Salutations\n' + prenom + ' ' + nom,
          evt: [],
          subject: `${copro?.nom} > ${demande.equipementlibelle} > ${demande.description}`.trim() + ` (${demande.id})`,
          to: [],
          cc: [],
        }}
      >
        <Form.Item name="to" hidden />
        {/* <Form.Item name="cc" hidden /> */}
        <Form.Item name="toMail" label="A">
          <DebounceSelect
            onSelect={(_, obj) => {
              const nextToo = [...form.getFieldValue('to')]
              nextToo.push(obj)
              form.setFieldsValue({ to: nextToo })
            }}
            onDeselect={(value) => {
              const nextToo = [...form.getFieldValue('to')].filter((t) => t.value !== value)
              form.setFieldsValue({ to: nextToo })
            }}
            placeholder="Recherche"
            mode="tags"
            fetchOptions={handleSearch}
            optionsFilter={optionsFilter('to')}
          />
        </Form.Item>
        {/* <Form.Item name="ccMail" label="CC">
          <DebounceSelect
            onSelect={(_, obj) => {
              const nextToo = [...form.getFieldValue('cc')]
              nextToo.push(obj)
              form.setFieldsValue({ cc: nextToo })
            }}
            onDeselect={(value) => {
              const nextToo = [...form.getFieldValue('cc')].filter((t) => t.value !== value)
              form.setFieldsValue({ cc: nextToo })
            }}
            placeholder="Recherche"
            mode="tags"
            fetchOptions={handleSearch}
            optionsFilter={optionsFilter('cc')}
          />
        </Form.Item> */}
        <Form.Item name="subject" label="Objet">
          <Input />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 2, span: 22 }}>
          <Select
            placeholder="Modèle de courriel"
            showSearch
            clearIcon
            allowClear
            onChange={(obj) =>
              form.setFieldsValue({ body: modeleCourrier?.modeles.find((m) => m.objet === obj)?.corpsdetexte })
            }
          >
            {modeleCourrier?.modeles?.map((m) => (
              <Select.Option key={m.id} value={m.objet ?? ''}>
                <Typography.Text strong>{m.objet}</Typography.Text>
                <Typography.Paragraph type="secondary" ellipsis={{ rows: 1 }}>
                  {m.corpsdetexte}
                </Typography.Paragraph>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="body" label="Message">
          <Input.TextArea rows={12} />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 2, span: 24 }}>
          <Row justify="space-between">
            <Button htmlType="submit" type="primary">
              Envoyer
            </Button>
          </Row>
        </Form.Item>
        <Form.Item name="evt">
          <Checkbox.Group>
            {evenements?.map((evt) => (
              <>
                <Checkbox value={evt.id} key={evt.id} style={{ marginLeft: 0 }}>
                  {evt.fichier ? (
                    <>
                      {evt.texte}
                      <Thumbnail texte={evt.texte} />
                    </>
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: evt.texte?.trim() ?? '' }} />
                  )}
                </Checkbox>
                <Divider />
              </>
            ))}
          </Checkbox.Group>
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 22 }}>
          <Button htmlType="submit" type="primary">
            Envoyer
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ModalSelectEvents
